/**
 * Cloud-Agent-Karte: Graph-Aufbau + organisches Layout.
 *
 * Reine Funktionen, kein React — damit sie testbar sind und das Rendering
 * nichts rechnen muss. Das Layout ist eine kleine Kraftsimulation (Abstossung,
 * Federn, Zentrierung) mit FESTEM Zufall (Seed aus der Knoten-ID): derselbe
 * Fall sieht nach jedem Reload gleich aus, wirkt aber gewachsen statt
 * ausgerichtet — genau das soll die Karte zeigen.
 *
 * Struktur: Ticket (Hub) ← Aktionen/Gedanken/Entwürfe (Satelliten)
 *           Ticket (Hub) ← interne Konversation = eigener Hub mit Satelliten
 */

export type AgentNodeKind =
  | 'case' | 'child' | 'thought' | 'action' | 'draft' | 'note'
  | 'status' | 'reply' | 'waiting' | 'result';

export interface AgentGraphNode {
  id: string;
  kind: AgentNodeKind;
  /** Hub-Knoten (Tickets) sind gross und ziehen ihre Satelliten an. */
  hub: boolean;
  title: string;
  subtitle?: string;
  time?: string;
  caseId: string;
  parentId?: string;
  status?: string;
  pending?: boolean;
  running?: boolean;
  failed?: boolean;
  badges?: string[];
  ticketId?: string;
  raw?: unknown;
}

export interface AgentGraphEdge {
  id: string;
  from: string;
  to: string;
  kind: 'satellite' | 'branch' | 'sequence';
  pending?: boolean;
}

export interface AgentGraph {
  nodes: AgentGraphNode[];
  edges: AgentGraphEdge[];
}

export interface Point { x: number; y: number }

/** Deterministischer Zufall: gleiche ID → gleiche Zahlenfolge. */
export function seedFrom(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const clean = (v?: string | null) => String(v ?? '').replace(/\s+/g, ' ').trim();
const short = (v?: string | null, max = 70) => {
  const s = clean(v);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

export interface CaseSummaryInput {
  id: string;
  ticket_id: string;
  ticket_number: string;
  subject: string;
  status: string;
  summary?: string;
  internal_comm_count?: number;
  updated_at?: string;
}

export interface CaseEventInput {
  kind: string;
  content?: string;
  metadata?: Record<string, unknown> | null;
  created_at: string;
}

export interface CaseTriggerInput {
  trigger_type: string;
  status: string;
  fire_at?: string | null;
  watch_ticket_number?: string | null;
}

export interface CaseDetailInput {
  case: CaseSummaryInput;
  events: CaseEventInput[];
  triggers: CaseTriggerInput[];
}

const EVENT_KIND: Record<string, AgentNodeKind> = {
  thought: 'thought',
  action: 'action',
  draft: 'draft',
  note: 'note',
  status: 'status',
  reply_received: 'reply',
};

/**
 * Baut die Karte aus der Fallliste und den bereits geladenen Details.
 * Fälle ohne Detail bleiben ein einzelner Hub (sie werden beim Klick geladen).
 */
export function buildAgentGraph(
  cases: CaseSummaryInput[],
  details: Record<string, CaseDetailInput | undefined>,
  labels: Record<string, string>,
  fmtTime: (iso?: string | null) => string,
): AgentGraph {
  const nodes: AgentGraphNode[] = [];
  const edges: AgentGraphEdge[] = [];

  for (const c of cases) {
    const caseNodeId = `case:${c.ticket_id}`;
    const detail = details[c.ticket_id];
    nodes.push({
      id: caseNodeId,
      kind: 'case',
      hub: true,
      title: c.ticket_number,
      subtitle: short(c.subject, 64),
      caseId: c.ticket_id,
      ticketId: c.ticket_id,
      status: c.status,
      running: !['done', 'failed'].includes(c.status),
      failed: c.status === 'failed',
      time: fmtTime(c.updated_at),
      badges: c.internal_comm_count ? [`${c.internal_comm_count}×`] : undefined,
      raw: c,
    });

    if (!detail) continue;

    // Interne Konversationen sind eigene Tickets → eigene Hubs am Fall.
    const childByNumber = new Map<string, string>();
    let lastSpine = caseNodeId;

    detail.events.forEach((ev, index) => {
      const meta = (ev.metadata || {}) as Record<string, any>;
      const id = `ev:${c.ticket_id}:${index}`;

      if (ev.kind === 'internal_comm') {
        const number = clean(meta.child_ticket_number);
        const childId = `child:${c.ticket_id}:${number || index}`;
        if (!nodes.some((n) => n.id === childId)) {
          nodes.push({
            id: childId,
            kind: 'child',
            hub: true,
            title: number || labels.internalComm,
            subtitle: clean(meta.department) || labels.internal,
            caseId: c.ticket_id,
            parentId: caseNodeId,
            time: fmtTime(ev.created_at),
            pending: meta.auto_send === false || /wartet auf Freigabe/i.test(clean(ev.content)),
            ticketId: meta.child_ticket_id ? String(meta.child_ticket_id) : undefined,
            badges: [labels.internalComm],
            raw: ev,
          });
          edges.push({
            id: `e:${caseNodeId}:${childId}`,
            from: caseNodeId,
            to: childId,
            kind: 'branch',
            pending: meta.auto_send === false,
          });
          if (number) childByNumber.set(number, childId);
        }
        return;
      }

      const kind = EVENT_KIND[ev.kind] || 'status';
      const badges: string[] = [];
      if (kind === 'draft') {
        if (meta.category) badges.push(String(meta.category));
        if (meta.confidence) badges.push(String(meta.confidence));
      }
      if (kind === 'action' && meta.attachment) badges.push(String(meta.attachment));

      // Antwort einer internen Konversation hängt am Kind-Ticket, nicht am Fall.
      const replyNumber = kind === 'reply' ? clean(meta.child_ticket_number) : '';
      const anchor = replyNumber && childByNumber.get(replyNumber)
        ? (childByNumber.get(replyNumber) as string)
        : caseNodeId;

      nodes.push({
        id,
        kind,
        hub: false,
        title: labels[kind] || kind,
        subtitle: short(ev.content, 78),
        caseId: c.ticket_id,
        parentId: anchor,
        time: fmtTime(ev.created_at),
        badges: badges.length ? badges : undefined,
        raw: ev,
      });
      edges.push({ id: `e:${anchor}:${id}`, from: anchor, to: id, kind: 'satellite' });
      // Dünne Sequenzkante: die Reihenfolge bleibt lesbar, ohne alles zu begradigen.
      if (lastSpine !== anchor && lastSpine !== id) {
        edges.push({ id: `s:${lastSpine}:${id}`, from: lastSpine, to: id, kind: 'sequence' });
      }
      lastSpine = id;
    });

    detail.triggers
      .filter((tr) => tr.status === 'pending')
      .forEach((tr, i) => {
        const id = `trg:${c.ticket_id}:${i}`;
        nodes.push({
          id,
          kind: 'waiting',
          hub: false,
          title: tr.trigger_type === 'time' ? labels.waitingTime : labels.waitingReply,
          subtitle: tr.trigger_type === 'time' ? fmtTime(tr.fire_at) : clean(tr.watch_ticket_number),
          caseId: c.ticket_id,
          parentId: caseNodeId,
          pending: true,
          raw: tr,
        });
        edges.push({ id: `e:${caseNodeId}:${id}`, from: caseNodeId, to: id, kind: 'satellite', pending: true });
      });
  }

  return { nodes, edges };
}

/* Ticket-Hubs bilden das Zentrum eines echten Datenclusters. Ereignisse sind
   kleine Neuronen darum herum; die Ellipsen verhindern Kartenkollisionen. */
const HUB_RX = 154;
const HUB_RY = 54;
const SAT_RX = 94;
const SAT_RY = 32;
const SAT_LINK = 154;
const CHILD_LINK = 238;
const CASE_GAP = 430;

export interface LayoutOptions {
  iterations?: number;
  /** Vorherige Positionen: bekannte Knoten bleiben stehen, nur Neue wandern. */
  previous?: Record<string, Point>;
}

/**
 * Organisches Layout. Fälle bekommen weit auseinanderliegende Startpunkte
 * (goldener Winkel), Satelliten starten auf einem Kreis um ihren Hub mit
 * gewürfeltem Winkel; danach glättet die Simulation Überlappungen.
 */
export function layoutAgentGraph(graph: AgentGraph, options: LayoutOptions = {}): Record<string, Point> {
  const { nodes, edges } = graph;
  const iterations = options.iterations ?? 260;
  const previous = options.previous || {};
  const pos: Record<string, Point> = {};
  const isHub: Record<string, boolean> = {};
  const rx = (id: string) => (isHub[id] ? HUB_RX : SAT_RX);
  const ry = (id: string) => (isHub[id] ? HUB_RY : SAT_RY);

  const caseIds = [...new Set(nodes.filter((n) => n.kind === 'case').map((n) => n.caseId))];
  const caseAnchor: Record<string, Point> = {};
  caseIds.forEach((caseId, i) => {
    const angle = i * 2.399963;
    const radius = i === 0 ? 0 : CASE_GAP * Math.sqrt(i);
    caseAnchor[caseId] = { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
  });

  for (const node of nodes) {
    isHub[node.id] = node.hub;
    const known = previous[node.id];
    if (known) { pos[node.id] = { ...known }; continue; }
    const rnd = mulberry32(seedFrom(node.id));
    const anchor = caseAnchor[node.caseId] || { x: 0, y: 0 };
    if (node.kind === 'case') {
      pos[node.id] = { ...anchor };
    } else {
      const parent = node.parentId ? previous[node.parentId] || pos[node.parentId] : undefined;
      const base = parent || anchor;
      const angle = rnd() * Math.PI * 2;
      const dist = (node.kind === 'child' ? CHILD_LINK : SAT_LINK) * (0.85 + rnd() * 0.5);
      pos[node.id] = { x: base.x + Math.cos(angle) * dist, y: base.y + Math.sin(angle) * dist };
    }
  }

  const adjacency = edges
    .filter((e) => e.kind !== 'sequence') // Sequenzkanten sind nur Optik, keine Kraft
    .map((e) => ({
      from: e.from,
      to: e.to,
      length: e.kind === 'branch' ? CHILD_LINK : SAT_LINK,
    }));

  for (let step = 0; step < iterations; step += 1) {
    const cooling = 1 - step / iterations;
    const force: Record<string, Point> = {};
    for (const n of nodes) force[n.id] = { x: 0, y: 0 };

    // Abstossung (O(n²) — bei den Grössenordnungen hier völlig ausreichend).
    // Gerechnet wird im normierten Raum (dx/rxSum, dy/rySum): dort ist die
    // Karte ein Kreis, und ein Wert < 1 heisst „die Karten überlappen".
    for (let i = 0; i < nodes.length; i += 1) {
      for (let j = i + 1; j < nodes.length; j += 1) {
        const a = nodes[i];
        const b = nodes[j];
        const pa = pos[a.id];
        const pb = pos[b.id];
        let dx = pa.x - pb.x;
        let dy = pa.y - pb.y;
        if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) {
          dx = ((seedFrom(a.id) % 11) - 5) || 1;
          dy = ((seedFrom(b.id) % 11) - 5) || 1;
        }
        const rxSum = rx(a.id) + rx(b.id);
        const rySum = ry(a.id) + ry(b.id);
        const ux = dx / rxSum;
        const uy = dy / rySum;
        const nd = Math.hypot(ux, uy) || 0.001;
        const sameCase = a.caseId === b.caseId;
        const reach = sameCase ? 1.15 : 1.42;
        if (nd < reach) {
          const push = (reach - nd) * (sameCase ? 30 : 46);
          force[a.id].x += (ux / nd) * push;
          force[a.id].y += (uy / nd) * push;
          force[b.id].x -= (ux / nd) * push;
          force[b.id].y -= (uy / nd) * push;
        }
      }
    }

    // Federn entlang der Kanten
    for (const link of adjacency) {
      const pa = pos[link.from];
      const pb = pos[link.to];
      if (!pa || !pb) continue;
      const dx = pb.x - pa.x;
      const dy = pb.y - pa.y;
      const dist = Math.hypot(dx, dy) || 1;
      const pull = (dist - link.length) * 0.09;
      const fx = (dx / dist) * pull;
      const fy = (dy / dist) * pull;
      force[link.from].x += fx;
      force[link.from].y += fy;
      force[link.to].x -= fx;
      force[link.to].y -= fy;
    }

    // Jeder Fall zieht seine Knoten sanft zu seinem Anker → Fälle vermischen sich nicht
    for (const node of nodes) {
      const anchor = caseAnchor[node.caseId];
      if (!anchor) continue;
      const p = pos[node.id];
      const weight = node.kind === 'case' ? 0.05 : 0.012;
      force[node.id].x += (anchor.x - p.x) * weight;
      force[node.id].y += (anchor.y - p.y) * weight;
    }

    const maxStep = 26 * cooling + 2;
    for (const node of nodes) {
      const f = force[node.id];
      const len = Math.hypot(f.x, f.y) || 1;
      const capped = Math.min(len, maxStep);
      pos[node.id].x += (f.x / len) * capped;
      pos[node.id].y += (f.y / len) * capped;
    }
  }

  return pos;
}

/** Bounding-Box aller Knoten inklusive Rand — für Auto-Zoom/Zentrierung. */
export function graphBounds(pos: Record<string, Point>, padding = 180) {
  const points = Object.values(pos);
  if (!points.length) return { minX: 0, minY: 0, maxX: 800, maxY: 600, width: 800, height: 600 };
  const minX = Math.min(...points.map((p) => p.x)) - padding;
  const minY = Math.min(...points.map((p) => p.y)) - padding;
  const maxX = Math.max(...points.map((p) => p.x)) + padding;
  const maxY = Math.max(...points.map((p) => p.y)) + padding;
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/** Weiche Kurve zwischen zwei Punkten (leicht ausgelenkt, damit nichts gerade wirkt). */
export function synapsePath(a: Point, b: Point, bend = 0.18): string {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const mx = a.x + dx / 2 - dy * bend;
  const my = a.y + dy / 2 + dx * bend;
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
}
